import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { NAV_ITEMS, ROUTES } from '../../constants';
import { FOLLOW_GRAPH_UPDATED_EVENT, authApi } from '../../services/authApi';
import streamApi from '../../services/streamApi';
import { readStoredUser } from '../../utils/navbarUser';
import { hasDirectAdminAccess } from '../../utils/adminAccess';
import './LeftSidebar.css';

/**
 * Number of streamers to show before "Show More" is clicked.
 * Keeps the sidebar scannable without overwhelming the user.
 */
const INITIAL_VISIBLE_COUNT = 5;

const LIVE_STATUS_POLL_MS = 30000;

const SIDEBAR_AVATAR_FALLBACKS = {
  spaced: 'https://i.pravatar.cc/150?u=spaced',
  imhere: 'https://i.pravatar.cc/150?u=imhere',
  'not today': 'https://i.pravatar.cc/150?u=nottoday',
  bert: 'https://i.pravatar.cc/150?u=bert',
  void: 'https://i.pravatar.cc/150?u=void',
};

const formatViewerMeta = (count) => {
  if (typeof count !== 'number' || Number.isNaN(count) || count <= 0) {
    return null;
  }

  if (count >= 1000) {
    const compact = count >= 10000
      ? `${Math.round(count / 1000)}K`
      : `${(count / 1000).toFixed(1).replace(/\.0$/, '')}K`;
    return `${compact} viewers`;
  }

  return `${count} viewers`;
};

/**
 * Maps icon names to their visual representations.
 * Using simple text icons for now — could be replaced with an icon library.
 */
const ICON_MAP = {
  home: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  broadcast: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="2" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49" />
      <path d="M7.76 16.24a6 6 0 0 1 0-8.49" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      <path d="M4.93 19.07a10 10 0 0 1 0-14.14" />
    </svg>
  ),
  upload: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  ),
  chat: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  ),
  shield: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  community: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  star: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  ),
};

/**
 * Left sidebar navigation component.
 *
 * Contains:
 * - Main navigation menu (Home, Go Live!, etc.)
 * - Following section (streamers the user follows)
 * - Currently Live section (all streamers live right now across the platform)
 *
 * Each section is collapsible via "Show More" functionality.
 *
 * @returns {JSX.Element}
 */
function LeftSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [followingExpanded, setFollowingExpanded] = useState(false);
  const [liveExpanded, setLiveExpanded] = useState(false);
  const [isAdmin] = useState(() => hasDirectAdminAccess());
  const [followingStreamers, setFollowingStreamers] = useState([]);
  const [currentlyLiveStreamers, setCurrentlyLiveStreamers] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const loadSidebar = async () => {
      const storedUser = readStoredUser();
      const currentUsername = (storedUser?.username || '').trim();

      try {
        const [followingData, liveStreams] = await Promise.all([
          currentUsername
            ? authApi.getFollowingList(currentUsername).catch(() => ({ following: [] }))
            : Promise.resolve({ following: [] }),
          streamApi.getLiveStreams().catch(() => []),
        ]);

        if (cancelled) return;

        const following = Array.isArray(followingData?.following)
          ? followingData.following.map((entry) => ({
              id: entry.id,
              name: entry.username,
              avatar: entry.profileImage || null,
              meta: '',
            }))
          : [];

        const live = Array.isArray(liveStreams)
          ? liveStreams.map((stream) => ({
              id: stream.userId,
              name: stream.username,
              avatar: stream.avatarUrl || null,
              meta: formatViewerMeta(stream.viewerCount) || 'Live now',
            }))
          : [];

        setFollowingStreamers(following);
        setCurrentlyLiveStreamers(live);
      } catch (error) {
        console.error('Failed to load sidebar data:', error);
        if (!cancelled) {
          setFollowingStreamers([]);
          setCurrentlyLiveStreamers([]);
        }
      }
    };

    loadSidebar();

    const handleFollowGraphUpdated = () => {
      loadSidebar();
    };

    window.addEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);
    const intervalId = setInterval(loadSidebar, LIVE_STATUS_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      window.removeEventListener(FOLLOW_GRAPH_UPDATED_EVENT, handleFollowGraphUpdated);
    };
  }, [location.pathname]);

  const navItems = isAdmin
    ? [...NAV_ITEMS, { id: 'admin', label: 'Admin', icon: 'shield', path: ROUTES.ADMIN }]
    : NAV_ITEMS;

  const visibleFollowing = followingExpanded
    ? followingStreamers
    : followingStreamers.slice(0, INITIAL_VISIBLE_COUNT);

  const visibleLive = liveExpanded
    ? currentlyLiveStreamers
    : currentlyLiveStreamers.slice(0, INITIAL_VISIBLE_COUNT);

  return (
    <aside className="left-sidebar" aria-label="Main navigation">
      {/* Main Navigation Menu */}
      <nav className="left-sidebar__nav">
        {navItems.map((item) => (
          <NavLink
            key={item.id}
            to={item.path}
            className={({ isActive }) =>
              `left-sidebar__nav-item ${isActive ? 'left-sidebar__nav-item--active' : ''}`
            }
          >
            <span className="left-sidebar__nav-icon" aria-hidden="true">
              {ICON_MAP[item.icon] || '•'}
            </span>
            <span className="left-sidebar__nav-label">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Following Section */}
      <section className="left-sidebar__section">
        <h2 className="left-sidebar__section-header">Following</h2>
        <ul className="left-sidebar__streamer-list">
          {visibleFollowing.length === 0 && (
            <li className="left-sidebar__empty">No followed streamers yet</li>
          )}
          {visibleFollowing.map((streamer) => (
            <StreamerListItem
              key={streamer.id}
              streamer={streamer}
              onClick={() => navigate(`/profile/${streamer.name}`)}
            />
          ))}
        </ul>
        {followingStreamers.length > INITIAL_VISIBLE_COUNT && (
          <button
            type="button"
            className="left-sidebar__show-more"
            onClick={() => setFollowingExpanded(!followingExpanded)}
            aria-expanded={followingExpanded}
          >
            {followingExpanded ? '▲ Show Less' : '▼ Show More'}
          </button>
        )}
      </section>

      {/* Currently Live Section */}
      <section className="left-sidebar__section">
        <h2 className="left-sidebar__section-header">Currently Live</h2>
        <ul className="left-sidebar__streamer-list">
          {visibleLive.length === 0 && (
            <li className="left-sidebar__empty">No streamers are live right now</li>
          )}
          {visibleLive.map((streamer) => (
            <StreamerListItem
              key={streamer.id}
              streamer={streamer}
              isLive
              onClick={() => navigate(`/profile/${streamer.name}`)}
            />
          ))}
        </ul>
        {currentlyLiveStreamers.length > INITIAL_VISIBLE_COUNT && (
          <button
            type="button"
            className="left-sidebar__show-more"
            onClick={() => setLiveExpanded(!liveExpanded)}
            aria-expanded={liveExpanded}
          >
            {liveExpanded ? '▲ Show Less' : '▼ Show More'}
          </button>
        )}
      </section>
    </aside>
  );
}

/**
 * Individual streamer item in the sidebar list.
 *
 * Displays avatar, name, and either follower count or viewer count.
 * Live streamers show a red "LIVE" indicator.
 *
 * @param {Object} props
 * @param {Object} props.streamer - Streamer data object
 * @param {boolean} [props.showFollowers] - Show follower count
 * @param {boolean} [props.showViewers] - Show viewer count
 * @param {boolean} [props.isLive] - Show live indicator
 */
function StreamerListItem({ streamer, isLive, onClick }) {
  const avatarUrl = streamer.avatar || SIDEBAR_AVATAR_FALLBACKS[(streamer.name || '').toLowerCase()] || null;

  return (
    <li>
      <button
        type="button"
        className={`left-sidebar__streamer-item ${isLive ? 'left-sidebar__streamer-item--live' : ''}`}
        onClick={onClick}
      >
        <div className={`left-sidebar__streamer-avatar ${isLive ? 'left-sidebar__streamer-avatar--live' : ''}`}>
          {avatarUrl ? (
            <img src={avatarUrl} alt="" referrerPolicy="no-referrer" />
          ) : (
            <span className="left-sidebar__avatar-placeholder">
              {streamer.name.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="left-sidebar__streamer-info">
          <span className="left-sidebar__streamer-name">
            {streamer.name}
            {isLive && <span className="left-sidebar__live-dot" aria-label="Live" />}
          </span>
          {streamer.meta && (
            <span className={`left-sidebar__streamer-meta ${isLive ? 'left-sidebar__streamer-meta--live' : ''}`}>
              {streamer.meta}
            </span>
          )}
        </div>
      </button>
    </li>
  );
}

StreamerListItem.propTypes = {
  streamer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    avatar: PropTypes.string,
    meta: PropTypes.string,
  }).isRequired,
  isLive: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

export default LeftSidebar;
